import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Meeting, CreateMeetingForm, MeetingFilters, MeetingAttendee, AttendeeResponse } from '../types/meetings';

interface MeetingState {
  meetings: Meeting[];
  currentMeeting: Meeting | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchMeetings: (filters?: MeetingFilters) => Promise<void>;
  fetchMeeting: (id: string) => Promise<void>;
  createMeeting: (meeting: CreateMeetingForm) => Promise<Meeting>;
  updateMeeting: (id: string, updates: Partial<Meeting>) => Promise<void>;
  updateMeetingStatus: (id: string, status: string) => Promise<void>;
  updateAttendeeResponse: (meetingId: string, userId: string, response: AttendeeResponse) => Promise<void>;
  deleteMeeting: (id: string) => Promise<void>;
}

export const useMeetingStore = create<MeetingState>((set, get) => ({
  meetings: [],
  currentMeeting: null,
  isLoading: false,
  error: null,

  fetchMeetings: async (filters?: MeetingFilters) => {
    set({ isLoading: true, error: null });
    try {
      // First, fetch appointments
      let query = supabase
        .from('appointments')
        .select('*')
        .order('start_date', { ascending: true });

      if (filters?.from) {
        query = query.gte('start_date', filters.from);
      }
      if (filters?.to) {
        query = query.lte('start_date', filters.to);
      }
      if (filters?.status?.length) {
        query = query.in('status', filters.status);
      }

      const { data: appointments, error } = await query;
      if (error) throw error;

      if (!appointments || appointments.length === 0) {
        set({ meetings: [], isLoading: false });
        return;
      }

      // Fetch creators
      const creatorIds = [...new Set(appointments.map(a => a.created_by))];
      const { data: creators } = await supabase
        .from('users')
        .select('*')
        .in('id', creatorIds);

      // Fetch attendees separately
      const appointmentIds = appointments.map(a => a.id);
      const { data: attendees } = await supabase
        .from('appointment_attendees')
        .select('*')
        .in('appointment_id', appointmentIds);

      // Fetch user details for attendees
      const attendeeUserIds = [...new Set(attendees?.map(a => a.user_id) || [])];
      const { data: attendeeUsers } = await supabase
        .from('users')
        .select('*')
        .in('id', attendeeUserIds);

      // Combine data
      const meetings = appointments.map(appointment => {
        const creator = creators?.find(c => c.id === appointment.created_by);
        const appointmentAttendees = attendees?.filter(a => a.appointment_id === appointment.id) || [];
        const attendeesWithUsers = appointmentAttendees.map(attendee => ({
          ...attendee,
          user: attendeeUsers?.find(u => u.id === attendee.user_id)
        }));

        return {
          ...appointment,
          creator,
          attendees: attendeesWithUsers
        };
      });

      set({ meetings, isLoading: false });
    } catch (error: any) {
      console.error('Fetch meetings error:', error);
      set({ error: error.message, isLoading: false });
    }
  },

  fetchMeeting: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // Fetch appointment
      const { data: appointment, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!appointment) {
        set({ currentMeeting: null, error: 'Meeting not found', isLoading: false });
        return;
      }

      // Fetch creator
      const { data: creator } = await supabase
        .from('users')
        .select('*')
        .eq('id', appointment.created_by)
        .maybeSingle();

      // Fetch attendees
      const { data: attendees } = await supabase
        .from('appointment_attendees')
        .select('*')
        .eq('appointment_id', id);

      // Fetch attendee users
      const attendeeUserIds = attendees?.map(a => a.user_id) || [];
      const { data: attendeeUsers } = await supabase
        .from('users')
        .select('*')
        .in('id', attendeeUserIds);

      // Combine data
      const attendeesWithUsers = attendees?.map(attendee => ({
        ...attendee,
        user: attendeeUsers?.find(u => u.id === attendee.user_id)
      })) || [];

      const meeting = {
        ...appointment,
        creator,
        attendees: attendeesWithUsers
      };

      set({ currentMeeting: meeting, isLoading: false });
    } catch (error: any) {
      console.error('Fetch meeting error:', error);
      set({ error: error.message, isLoading: false });
    }
  },

  createMeeting: async (meetingData: CreateMeetingForm) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get current user profile
      const { data: userProfile } = await supabase
        .from('users')
        .select('profile_type')
        .eq('id', user.id)
        .maybeSingle();

      // Validate porteur rule: cannot invite only other porteurs
      if (userProfile?.profile_type === 'PORTEUR') {
        const { data: attendees } = await supabase
          .from('users')
          .select('profile_type')
          .in('id', meetingData.attendee_ids);

        const hasNonPorteur = attendees?.some(attendee => attendee.profile_type !== 'PORTEUR');
        if (!hasNonPorteur) {
          throw new Error('En tant que porteur, vous devez inviter au moins un accompagnateur, coordinateur, financeur ou administrateur.');
        }
      }

      // Create meeting
      const { data: meeting, error: meetingError } = await supabase
        .from('appointments')
        .insert({
          title: meetingData.title,
          description: meetingData.description,
          start_date: meetingData.start_date,
          end_date: meetingData.end_date,
          location: meetingData.location || null,
          meeting_url: meetingData.video_link || null,
          project_id: meetingData.project_id || null,
          created_by: user.id,
          status: 'PLANIFIE',
          subject_type: meetingData.subject_type || null
        })
        .select()
        .maybeSingle();

      if (meetingError) {
        console.error('Meeting creation error:', meetingError);
        throw meetingError;
      }
      if (!meeting) throw new Error('Failed to create meeting');

      console.log('Meeting created:', meeting);

      // Add creator as organizer + attendees
      const allAttendeeIds = [user.id, ...meetingData.attendee_ids.filter(id => id !== user.id)];
      const attendeeInserts = allAttendeeIds.map(userId => ({
        appointment_id: meeting.id,
        user_id: userId,
        is_organizer: userId === user.id,
        response: userId === user.id ? 'accepted' : null
      }));

      const { error: attendeesError } = await supabase
        .from('appointment_attendees')
        .insert(attendeeInserts);

      if (attendeesError) {
        console.error('Attendees insertion error:', attendeesError);
        throw attendeesError;
      }

      console.log('Attendees added successfully');

      // Create notifications for attendees
      const notificationInserts = meetingData.attendee_ids
        .filter(id => id !== user.id)
        .map(userId => ({
          user_id: userId,
          type: 'APPOINTMENT_SCHEDULED',
          title: 'Nouveau rendez-vous',
          message: `${userProfile?.profile_type === 'PORTEUR' ? 'Un porteur' : 'Un utilisateur'} vous a invité à un rendez-vous: ${meetingData.title}`,
          link: `/rdv/${meeting.id}`,
          metadata: {
            meeting_id: meeting.id,
            meeting_title: meetingData.title,
            start_date: meetingData.start_date
          }
        }));

      if (notificationInserts.length > 0) {
        const { error: notifError } = await supabase
          .from('notifications_enhanced')
          .insert(notificationInserts);

        if (notifError) {
          console.error('Notification creation error:', notifError);
          // Don't throw - notifications are not critical
        }
      }

      // Refresh meetings list
      await get().fetchMeetings();

      set({ isLoading: false });
      return meeting;
    } catch (error: any) {
      console.error('Create meeting failed:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateMeeting: async (id: string, updates: Partial<Meeting>) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('appointments')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      // Update local state
      const { meetings, currentMeeting } = get();
      const updatedMeetings = meetings.map(m => 
        m.id === id ? { ...m, ...updates } : m
      );
      
      set({
        meetings: updatedMeetings,
        currentMeeting: currentMeeting?.id === id 
          ? { ...currentMeeting, ...updates } 
          : currentMeeting,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  updateMeetingStatus: async (id: string, status: string) => {
    await get().updateMeeting(id, { status: status as any });
  },

  updateAttendeeResponse: async (meetingId: string, userId: string, response: AttendeeResponse) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('appointment_attendees')
        .update({ response })
        .eq('appointment_id', meetingId)
        .eq('user_id', userId);

      if (error) throw error;

      // Refresh current meeting to get updated status (trigger handles status update)
      const { currentMeeting } = get();
      if (currentMeeting?.id === meetingId) {
        await get().fetchMeeting(meetingId);
      }

      // Also refresh the meetings list
      await get().fetchMeetings();

      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  deleteMeeting: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const { meetings } = get();
      set({ 
        meetings: meetings.filter(m => m.id !== id),
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  }
}));